'use client';

import { ErrorSection } from '@pickleballinc/react-ui';
import { useSession } from '@pickleballinc/ui/components/global/SessionProvider';

import { usePlayerLeagues } from '@/hooks/players/useGetPlayerLeagues';
import { FilterTabs } from '@/modules/players/v2/navigation/FilterTabs';
import { EventCardsListSkeleton } from '@/modules/players/v2/navigation/skeletons/EventCardsListSkeleton';
import { LeagueWrapper } from '@/modules/players/v2/tabs/league/LeagueWrapper';
import { LeaguesStatus } from '@/types/leagues.types';
import { PlayerData, PlayerParticipationType } from '@/types/players.types';
import { GetPBApproveToCreatePermissionRes } from '@/utils/actions/leagues/getPBApproveToCreatePermission';

interface LeaguesClientWrapperProps {
  player: PlayerData;
  activeLeagueType: PlayerParticipationType;
  status: LeaguesStatus;
  searchParams: {
    [key: string]: string;
  };
  createPerms: GetPBApproveToCreatePermissionRes;
}

export const LeaguesClientWrapper = ({
  player,
  activeLeagueType,
  status,
  searchParams,
  createPerms,
}: LeaguesClientWrapperProps) => {
  const { session } = useSession();

  const {
    data: leaguesResult,
    isLoading,
    isFetching,
  } = usePlayerLeagues({
    playerId: player.pbUuid,
    activeLeagueType,
    status,
    searchParams,
  });

  if (!isLoading && leaguesResult?.error) {
    return (
      <ErrorSection
        Subheading='Please reach out to our support to report this issue'
        heading='Something wrong happened'
      />
    );
  }

  const leaguesData = leaguesResult?.data;
  const hasLeagues = leaguesData ? leaguesData.totalRecords > 0 : false;

  return (
    <>
      <FilterTabs hasData={hasLeagues} type='league' />

      <div className='container mx-auto mb-8 !max-w-[996px] rounded-md xl:!px-0'>
        {isLoading || isFetching ? (
          <EventCardsListSkeleton />
        ) : (
          <LeagueWrapper
            leagues={leaguesData?.leagues ?? []}
            player={player}
            user={session?.user}
            createPerms={createPerms}
            totalRecords={leaguesData?.totalRecords ?? 0}
          />
        )}
      </div>
    </>
  );
};
